// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSessionUserEmail, getUrlParameter } from '../config/common';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../hooks';
import * as requiredListAction from '../Action/Required_List/Required_List_Action';
import * as recentListAction from '../Action/Recent_List/Recent_List_Action';
import * as releaseNotesListAction from '../Action/Release_Notes_List/Release_Notes_List_Action';
import Loader from './Loader';
import RequestUpgradeModal from './RequestUpgradeModal';
import PendingAccountNotice from './PendingAccountNotice';
import { hasRole } from '../utils/roles';
import { readReqStatus } from '../utils/reqStatus';

const ANY_VERSION = '';

const ReleaseNotes = ({ roles }) => {
    const dispatch = useAppDispatch();
    const [selectedFixVersion, setSelectedFixVersion] = useState(ANY_VERSION);
    const [versionSectionsCache, setVersionSectionsCache] = useState({});
    const [userDetails, setUserDetails] = useState({});
    const [releaseNotesData, setReleaseNotesData] = useState({
        versions: [],
        configVersions: {}
    });
    const [recentReleaseNotes, setRecentReleaseNotes] = useState([]);
    const [releaseNotesMap, setReleaseNotesMap] = useState({});
    const [isCreateRequestModalShow, setIsCreateRequestModalShow] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const hasFetched = useRef();

    useEffect(() => {
        if(hasFetched.current) return;
        hasFetched.current = true;

        // ACOP-118: known-pending accounts never succeed at requiredlist/recentlist (both
        // minTier "standard"), so skip the round trip entirely rather than call-then-catch-403.
        if (readReqStatus() === 'Requested') {
            setUserDetails({ Status: 'Pending' });
            return;
        }

        setIsLoading(true);
        getRequiredData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyPatchHints = (releaseNotes, customerVersion) => {
        if (
            !releaseNotes ||
            releaseNotes.length === 0 ||
            !customerVersion ||
            customerVersion.length === 0
        ) {
            return releaseNotes;
        }
        releaseNotes.forEach((note) => {
            if (note.Issue_Type__c && note.Issue_Type__c === 'Patch') {
                note.customerVersion = customerVersion;
                let patchVersion = note.Build_Version_Patch__c || '';
                patchVersion = patchVersion.replace('Patch', '').trim();
                note.Build_Version_Patch__c = patchVersion;
                const result = compareVersions(customerVersion, patchVersion);
                if (result !== null && note.Issue_Type__c !== 'Story') {
                    note.hasPatch = result;
                }
            }
        });
        return releaseNotes;
    };

    const getRequiredData = async () => {
        const email = getSessionUserEmail();
        const param = { data: JSON.stringify({ email }) };
        try {
            /* Run in parallel — previously required_list then recent_list doubled wait time. */
            const [requiredResponse, recentResponse] = await Promise.all([
                dispatch(requiredListAction.required_list(param)),
                dispatch(recentListAction.recent_list(param)),
            ]);

            if (requiredResponse.status === 403) {
                setUserDetails({ Status: 'Pending' });
                setIsLoading(false);
                return;
            }

            if (requiredResponse.status !== 1) {
                setIsLoading(false);
                toast.error(requiredResponse.message || 'An error occurred');
                return;
            }

            const versions = requiredResponse.data.versions || [];
            const configVersionMap = {};
            versions.forEach((ver) => {
                configVersionMap[ver.fixVersion] = ver;
            });
            setReleaseNotesData({
                versions: versions,
                configVersions: configVersionMap,
            });
            requiredResponse.data.contact = requiredResponse.data.contact || {};
            requiredResponse.data.contact.Status = requiredResponse.data.status;
            setUserDetails(requiredResponse.data.contact || {});

            if (recentResponse.status !== 1) {
                toast.error(recentResponse.message);
                setIsLoading(false);
                return;
            }
            const customerVersion = getUrlParameter('version');
            let notes = recentResponse.data.releaseNotes;
            notes = applyPatchHints(notes, customerVersion);
            setRecentReleaseNotes(notes);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            toast.error(error.message);
        }
    };

    const compareVersions = (user, patch) => {
        const userParts = user.split(".").map(Number);
        const patchParts = patch.split(".").map(Number);

        if (userParts.length === 2) {
            userParts.push(0);
        }

        if (userParts[0] !== patchParts[0]) {
            return null;
        }

        if (patchParts[1] > userParts[1]) {
            return false;
        } else if (patchParts[1] < userParts[1]) {
            return true;
        }

        if (patchParts[2] > userParts[2]) {
            return false;
        } else if (patchParts[2] < userParts[2]) {
            return true;
        }
        return true;
    };

    const getVersionKeys = (obj) => {
        return Object.keys(obj);
    };

    const showCreateRequest = () => {
        setIsCreateRequestModalShow(true);
    };

    const handleSelectVersion = useCallback((fixVersion) => {
        if (!fixVersion) {
            setSelectedFixVersion(ANY_VERSION);
            setReleaseNotesMap({});
            return;
        }

        if (versionSectionsCache[fixVersion]) {
            setReleaseNotesMap(versionSectionsCache[fixVersion]);
            setSelectedFixVersion(fixVersion);
            return;
        }

        const param = {
            searchText: '',
            version: fixVersion,
            email: getSessionUserEmail(),
        };

        setIsLoading(true);
        dispatch(releaseNotesListAction.release_notes_list({ data: JSON.stringify(param) }))
            .then((response) => {
                setIsLoading(false);
                if (response.status !== 1) {
                    toast.error(response.message || 'Failed to load release notes');
                    return;
                }
                if (!response.data || !response.data.releaseNotes) {
                    toast.error('No release notes available for this version');
                    return;
                }

                const sectionMap = {};
                response.data.releaseNotes.forEach((item) => {
                    if (!sectionMap.hasOwnProperty(item.Release_Note_Section__c)) {
                        sectionMap[item.Release_Note_Section__c] = [];
                    }
                    sectionMap[item.Release_Note_Section__c].push(item);
                });

                setVersionSectionsCache((prev) => ({ ...prev, [fixVersion]: sectionMap }));
                setReleaseNotesMap(sectionMap);
                setSelectedFixVersion(fixVersion);
            })
            .catch((error) => {
                setIsLoading(false);
                toast.error(error?.message || 'Failed to load version history');
            });
    }, [dispatch, versionSectionsCache]);


    const selectedVersionInfo = selectedFixVersion
        ? releaseNotesData.configVersions[selectedFixVersion]
        : null;
    const isFiltered = Boolean(selectedFixVersion);

    const hideCreateRequest = () => {
        setIsCreateRequestModalShow(false);
    };

    const issueTypeLabel = (t) =>
        t === 'Patch' ? 'Feature patch' : t === 'Bug' ? 'Bug fix' : 'New feature';
    const issueTypeBadgeClass = (t) =>
        t === 'Patch' ? 'warning' : t === 'Bug' ? 'danger' : 'success';

    return (
        <>
            {isLoading && <Loader />}

            <div className="container-fluid py-3 px-3 px-md-4">
                {userDetails.Status === 'Pending' ? (
                    <PendingAccountNotice message="Once your account has been verified by an AvSight employee, you will be able to view the change log." />
                ) : (
                    <div className="copilot-filter-layout">
                        {/* Left rail: version picker + upgrade request */}
                        <aside className="copilot-filter-rail">
                            <h2 className="copilot-filter-rail__title">Version</h2>

                            <label className="d-flex align-items-center gap-2 mb-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="changelog-version"
                                    checked={!isFiltered}
                                    onChange={() => handleSelectVersion(ANY_VERSION)}
                                />
                                <span className="form-label mb-0">Last 50, all versions</span>
                            </label>
                            {releaseNotesData.versions.map((version) => (
                                <label key={version.fixVersion} className="d-flex align-items-center gap-2 mb-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="changelog-version"
                                        checked={version.fixVersion === selectedFixVersion}
                                        onChange={() => handleSelectVersion(version.fixVersion)}
                                    />
                                    <span className="form-label mb-0">{version.name}</span>
                                </label>
                            ))}

                            {userDetails.Status !== 'Requested' && hasRole(roles, 'support') && (
                                <>
                                    <hr className="my-3" />
                                    <p className="copilot-filter-rail__hint">
                                        All upgrades include prior features and fixes. You can skip versions and go to the latest without losing functionality.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={showCreateRequest}
                                    >
                                        <i className="fa-solid fa-arrow-up-right-from-square me-2" aria-hidden />
                                        Request Upgrade
                                    </button>
                                </>
                            )}
                        </aside>

                        {/* Main content: release notes without card wrappers */}
                        <main className="flex-grow-1 min-w-0 ms-lg-4 mt-3 mt-lg-0">
                            {isFiltered ? (
                                <>
                                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                                        <div className="min-w-0">
                                            <h2 className="copilot-changelog-section-title mb-1">
                                                AvSight {selectedVersionInfo?.name}
                                            </h2>
                                            <p className="copilot-changelog-section-lead mb-0">
                                                Release notes grouped by section
                                                {selectedVersionInfo?.versionNumber ? ` · ${selectedVersionInfo.versionNumber}` : ''}.
                                            </p>
                                        </div>
                                    </div>

                                    {getVersionKeys(releaseNotesMap).map((section, index) => (
                                        <div key={index} className="mb-4">
                                            <h3 className="copilot-changelog-detail-section-title">
                                                {section}
                                            </h3>
                                            <ul className="list-unstyled mb-0">
                                                {releaseNotesMap[section].map((item, idx) => (
                                                    <li key={idx} className="copilot-changelog-detail-note">
                                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                            <span
                                                                className={`badge rounded-pill copilot-changelog-type-badge bg-${issueTypeBadgeClass(item.Issue_Type__c)} ${item.Issue_Type__c === 'Patch' ? 'text-dark' : ''}`}
                                                            >
                                                                {issueTypeLabel(item.Issue_Type__c)}
                                                            </span>
                                                            {item.Jira_Number__c ? (
                                                                <span className="small text-body-secondary fw-semibold">
                                                                    {item.Jira_Number__c}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <p className="copilot-changelog-recent-item__body mb-0">
                                                            {item.Release_Notes__c}
                                                        </p>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <>
                                    <h2 className="copilot-changelog-section-title">Last 50, all versions</h2>
                                    <p className="copilot-changelog-section-lead">
                                        Last 50 updates across all supported versions. Select a version on the left to narrow the list to a single release. Patches roll to reporting customers on a regular cadence; critical patches apply to all orgs immediately.
                                    </p>
                                    <ul className="copilot-changelog-recent-list list-unstyled mb-0">
                                        {recentReleaseNotes.map((item, index) => (
                                            <li key={index} className="copilot-changelog-recent-item">
                                                <div className="copilot-changelog-recent-item__meta">
                                                    <span
                                                        className={`badge rounded-pill copilot-changelog-type-badge bg-${issueTypeBadgeClass(item.Issue_Type__c)} ${item.Issue_Type__c === 'Patch' ? 'text-dark' : ''}`}
                                                    >
                                                        {issueTypeLabel(item.Issue_Type__c)}
                                                    </span>
                                                    <span className="text-body-secondary fw-normal">
                                                        {(releaseNotesData.configVersions[item.Fix_Version__c]?.name || item.Fix_Version__c) + ' · '}
                                                        {item.Release_Note_Section__c}
                                                        {item.Jira_Number__c ? ` · ${item.Jira_Number__c}` : ''}
                                                    </span>
                                                    {item.hasOwnProperty('hasPatch') ? (
                                                        <span
                                                            className={item.hasPatch ? 'text-success' : 'text-warning'}
                                                            title={'Customer: ' + item.customerVersion + ', Patch: ' + item.Build_Version_Patch__c}
                                                        >
                                                            {item.hasPatch ? 'You have this patch' : "You don't have this patch"}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <p className="copilot-changelog-recent-item__body">
                                                    {item.Release_Notes__c}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </main>
                    </div>
                )}
            </div>

            {/* create update request */}
            {/* Shared with the Upgrade Requests screen — see RequestUpgradeModal. */}
            <RequestUpgradeModal
                show={isCreateRequestModalShow}
                versions={releaseNotesData.versions}
                onClose={hideCreateRequest}
            />
        </>
    )
}

export default ReleaseNotes;
